import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react'
import CalculatorModal from 'features/declaration/form/components/calculator/CalculatorModal'

export interface OpenOptions {
  onResolve: (value: number) => void
}
export interface CalculatorModalContextType {
  open: (options: OpenOptions) => void
  close: () => void
}
export const CalculatorModalContext = createContext<CalculatorModalContextType | null>(null)

export function CalculatorModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setOpen] = useState(false)
  const [options, setOptions] = useState<OpenOptions | null>(null)

  const open = useCallback((openOptions: OpenOptions) => {
    setOptions(openOptions)
    setOpen(true)
  }, [])

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  const resolveAndClose = useCallback((value: number) => {
    if (options?.onResolve) {
      options.onResolve(value)
    }
    close()
  }, [options])

  const value = useMemo(() => ({ open, close }), [open, close])

  return (
    <CalculatorModalContext.Provider value={value}>
      {children}
      <CalculatorModal isOpen={isOpen} close={close} onResolve={resolveAndClose} />
    </CalculatorModalContext.Provider>
  )
}

export function useCalculatorModal() {
  const context = useContext(CalculatorModalContext)
  if (!context) throw new Error('useCalculatorModal must be used within <CalculatorModalProvider>')
  return context
}
